import { ObservablePoint } from 'pixi.js';
import { onlyInLeft } from 'PreciateUtil';

import { MAX_DISTANCE, PLAYER_RADIUS } from '../../constants';
import { Player } from '../../social-pixi-canvas/player';
import { SceneManager } from '../../social-pixi-canvas/scene-manager';
import { UserId } from '../../types';

import {
  SUBSCRIPTION_SETTINGS,
  SubscriberState,
  SubscriptionReason,
  AudibleUser,
  VISIBLE_SUBSCRIPTIONS_LIMIT,
  SubscriptionOptions,
  DistanceUser,
  REASON_PRIORITY,
  GroupUser,
  DEFAULT_SUBSCRIPTION_LIMIT,
} from './subscription.types';

/**
 * Removes audio from subscription requirements.
 * @param subscriber A subscriber's subscription requirements.
 * @returns A modified list where no subscriber requires audio
 */
export const noAudio = (subscriber: SubscriberState) => ({ ...subscriber, audioRequired: false });
export const yesAudio = (subscriber: SubscriberState) => ({ ...subscriber, audioRequired: true });

/**
 * Removes audio and video from subscription requirements.
 * @param subscriber A subscriber's subscription requirements.
 * @returns A modified list where no subscriber requires audio
 */

/**
 * Prepares distance calculation between main player and a given player.
 *
 * @param player Player to calculate the distance
 * @param sceneManager SceneManager class
 * @returns the distance between main player and the given player
 */
export const getPlayerDistance = async (player: Player, sceneManager: SceneManager): Promise<number> => {
  const mainPlayer = await sceneManager.getPlayerAsync();
  const mainPlayerPosition = mainPlayer.position;
  return calculateDistance(mainPlayerPosition, player.position);
};

export const isPlayerInGroup = async (player: Player, sceneManager: SceneManager): Promise<boolean> => {
  const mainPlayer = await sceneManager.getPlayerAsync();
  const { groupId } = player.playerGroupInfo ?? {};
  return mainPlayer.groupId === groupId;
};

/**
 * Uses d=√((x_2-x_1)²+(y_2-y_1)²) to calculate distance
 * @param positionA Player A position
 * @param positionB Player B position
 * @returns the distance between A and B.
 */
export const calculateDistance = (positionA: ObservablePoint, positionB: ObservablePoint): number => {
  const x = (positionB.x - positionA.x) * (positionB.x - positionA.x);
  const y = (positionB.y - positionA.y) * (positionB.y - positionA.y);
  const distanceFromCenter = PLAYER_RADIUS * PLAYER_RADIUS; // account for sprite width and height spacing from true center
  const trueDistance = x + y - distanceFromCenter;
  return Math.sqrt(trueDistance);
};

export const sortSubscriptions = ({
  audible,
  audience,
  gallery,
  group,
  groupShares,
  options,
  presenters,
  speaking,
  visible,
}: {
  presenters: UserId[];
  audience: UserId[];
  gallery: UserId[];
  speaking: UserId[];
  group: GroupUser[];
  visible: DistanceUser[];
  audible: { userId: UserId; volume: number }[];
  groupShares: UserId[];
  options: SubscriptionOptions;
}): SubscriberState[] => {
  const {
    isPresentationActive,
    isToastActive,
    isUserInGroupShare,
    isUserPresenting,
    presentationSettings,
    speakingParticipantsV1,
    unrestrictedZoom,
  } = options;
  const { allowGroupConversations } = presentationSettings ?? { allowGroupConversations: true };
  const visibleIds = visible.map((v) => v.userId);

  const presenterSubscriptions = presenters.map((id) => ({
    id,
    ...SUBSCRIPTION_SETTINGS[SubscriptionReason.PRESENTER],
    reason: SubscriptionReason.PRESENTER,
    videoRequired: true,
  }));

  // Mapping users that are speaking during a presentation
  const speakingSubscriptions = speaking.map((id) => ({
    id,
    ...SUBSCRIPTION_SETTINGS[SubscriptionReason.SPEAKING],
    reason: SubscriptionReason.SPEAKING,
    videoRequired: speakingParticipantsV1
      ? videoRequiredForSpeakingParticipantsV1(isPresentationActive, visibleIds, speaking, id)
      : videoRequiredForSpeakingParticipants(isUserPresenting, audience, visibleIds, id),
  }));

  const gallerySubscriptions = gallery.slice(0, DEFAULT_SUBSCRIPTION_LIMIT).map((id) => ({
    id,
    ...SUBSCRIPTION_SETTINGS[SubscriptionReason.GALLERY],
    reason: SubscriptionReason.GALLERY,
    videoRequired: videoRequiredForGallery(id, speaking),
  }));

  const groupSubscriptions = group.map(({ userId, visible: userVisible }) => ({
    id: userId,
    ...SUBSCRIPTION_SETTINGS[SubscriptionReason.GROUP],
    audioRequired: audioRequiredForGroupV1(userId, isPresentationActive, allowGroupConversations),
    videoRequired: videoRequiredForGroupV1(isPresentationActive, userVisible),
    reason: SubscriptionReason.GROUP,
  }));

  const groupShareSubscriptions = groupShares.slice(0, DEFAULT_SUBSCRIPTION_LIMIT).map((id) => ({
    id,
    ...SUBSCRIPTION_SETTINGS[SubscriptionReason.GROUP_SHARE],
    reason: SubscriptionReason.GROUP_SHARE,
  }));

  const visibleSubscriptions = visible
    .sort(sortByDistance)
    .slice(0, unrestrictedZoom ? VISIBLE_SUBSCRIPTIONS_LIMIT : visible.length)
    .map(({ distance, userId }) => ({
      id: userId,
      ...SUBSCRIPTION_SETTINGS[SubscriptionReason.VISIBLE],
      audioRequired: audioRequiredForVisibleV1(userId, isPresentationActive, allowGroupConversations, distance),
      videoRequired: true,
      reason: SubscriptionReason.VISIBLE,
    }));

  const audibleSubscriptions = audible.sort(sortByVolume).map((a) => ({
    id: a.userId,
    ...SUBSCRIPTION_SETTINGS[SubscriptionReason.AUDIBLE],
    audioRequired: audioRequiredForAudibleV1(a.userId, isPresentationActive, allowGroupConversations),
    reason: SubscriptionReason.AUDIBLE,
  }));

  // When audio is mixed, pull audio subscription requirements some subscriptions.
  // Presenters have all audio removed, and only listen to the mix.
  // Audience members will potentially have group audio subscriptions if group conversations are allowed, and the mode
  // is not fully open or closed.

  // Active presentation
  if (isPresentationActive) {
    return removeDuplicatedIds([...gallerySubscriptions, ...speakingSubscriptions, ...presenterSubscriptions]).map(
      noAudio,
    );
  }

  // Active toast
  if (isToastActive) {
    return removeDuplicatedIds([
      ...visibleSubscriptions,
      ...groupSubscriptions,
      ...presenterSubscriptions.map(yesAudio),
    ]);
  }

  // If active Group Share, only show group share participants
  if (isUserInGroupShare) {
    return removeDuplicatedIds([...groupShareSubscriptions]);
  }

  // Default if no active presentation or group share, return all with active group share participants removed
  const groupShareIds = groupShareSubscriptions.map((s) => s.id);
  return removeDuplicatedIds(
    [...audibleSubscriptions, ...visibleSubscriptions, ...groupSubscriptions].filter(
      (s) => !groupShareIds.includes(s.id),
    ),
  );
};

const sortByDistance = (a: DistanceUser, b: DistanceUser): number => {
  return a.distance - b.distance;
};

const sortByVolume = (a: AudibleUser, b: AudibleUser): number => {
  return b.volume - a.volume;
};

const sortByPriority = (a: SubscriberState, b: SubscriberState): number => {
  return REASON_PRIORITY[b.reason] - REASON_PRIORITY[a.reason];
};

const audioRequiredByDistance = (distance: number): boolean => !(distance >= MAX_DISTANCE);

const audioRequiredForVisibleV1 = (
  id: string,
  isPresentationActive: boolean,
  allowGroupConversations: boolean,
  distance: number,
): boolean => {
  if (isPresentationActive) {
    return false;
  }

  return allowGroupConversations && audioRequiredByDistance(distance);
};

const audioRequiredForAudibleV1 = (
  id: string,
  isPresentationActive: boolean,
  allowGroupConversations: boolean,
): boolean => {
  if (isPresentationActive) {
    return false;
  }

  return allowGroupConversations;
};

const audioRequiredForGroupV1 = (
  id: string,
  isPresentationActive: boolean,
  allowGroupConversations: boolean,
): boolean => {
  if (isPresentationActive) {
    return false;
  }

  return allowGroupConversations;
};

const videoRequiredForGroupV1 = (isPresentationActive: boolean, userVisible: boolean): boolean => {
  if (isPresentationActive) {
    return false;
  }

  return userVisible;
};

const videoRequiredForSpeakingParticipantsV1 = (
  isPresentationActive: boolean,
  visibleIds: string[],
  speaking: string[],
  id: string,
): boolean => {
  const speakers = speaking.slice(0, 4);
  if (isPresentationActive) {
    return speakers.includes(id);
  }

  return visibleIds.includes(id);
};

const videoRequiredForSpeakingParticipants = (
  isUserPresenting: boolean,
  audience: string[],
  visibleIds: string[],
  id: string,
): boolean => {
  return isUserPresenting ? audience.includes(id) : visibleIds.includes(id);
};

const videoRequiredForGallery = (id: string, speaking: string[]): boolean => {
  const speakers = speaking.slice(0, 4);
  return !speakers.includes(id);
};

/**
 * Removes duplicated ids and keeps the ones with higher priority
 *
 * IMPORTANT: Send the array ordered from lower priority to higher priority
 * @param subscribers The list of all the subscriptions
 * @returns A new array with unique subscribers ordered by priority from higher to low
 */
const removeDuplicatedIds = (subscribers: SubscriberState[]) => {
  // TODO: added filter to avoid console error when subscriber is undefined. Do we need extra cleanup here?
  const unique = [...new Map(subscribers.filter(Boolean).map((item) => [item.id, item])).values()];
  return unique.sort(sortByPriority);
};

export const findNewSubscriptions = (prev: SubscriberState[], current: SubscriberState[]) =>
  onlyInLeft(
    current,
    prev,
    (a, b) =>
      a.id === b.id &&
      a.reason === b.reason &&
      a.audioRequired === b.audioRequired &&
      a.videoRequired === b.videoRequired,
  );

export const findRemovedSubscriptions = (prev: SubscriberState[], current: SubscriberState[]) =>
  onlyInLeft(prev, current, (a, b) => a.id === b.id);

export const getReason = (reason?: SubscriptionReason): string => {
  switch (reason) {
    case SubscriptionReason.GROUP:
      return 'is in group 🎉';
    case SubscriptionReason.GROUP_SHARE:
      return 'is in group share 🎉 🎤';
    case SubscriptionReason.AUDIBLE:
      return 'is in the audio range 🦻';
    case SubscriptionReason.AUDIENCE:
      return 'is in the audience 🪑';
    case SubscriptionReason.GALLERY:
      return 'is in the gallery 🪑';
    case SubscriptionReason.PRESENTER:
      return 'is presenting 🎤';
    case SubscriptionReason.SPEAKING:
      return 'is speaking 🗣';
    case SubscriptionReason.VISIBLE:
      return 'is visible 👀';
    default:
      return '';
  }
};

export const getLogComplement = (subscriber: SubscriberState) => {
  const { audioRequired, reason, videoRequired } = subscriber;
  return `- Reason: ${getReason(reason)}, Audio: ${audioRequired}, Video: ${videoRequired}`;
};
